import { Database, Globe2, Image, KeyRound, LineChart, LogIn, LogOut, Save, Settings, ShieldCheck } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { settingsGroups } from '../data/site';
import { envStatus } from '../env';
import { useAuth } from '../lib/auth';

export function AdminApp() {
  const { error, isLoading, signIn, signOut, user } = useAuth();

  if (isLoading) {
    return <div className="adminLoading">Admin wird geladen...</div>;
  }

  if (!user) {
    return <AdminLogin error={error} onSignIn={signIn} />;
  }

  return (
    <main className="adminShell">
      <aside className="adminSidebar">
        <a className="adminBrand" href="/">
          <img src="/seo-pictures-logo.svg" alt="SEO Pictures" />
        </a>
        <nav>
          <a className="active" href="/admin"><Settings size={18} /> Einstellungen</a>
          <a href="/admin"><Globe2 size={18} /> Seiten</a>
          <a href="/admin"><Image size={18} /> Medien</a>
          <a href="/admin"><LineChart size={18} /> Tracking</a>
          <a href="/admin"><Database size={18} /> Leads</a>
          <a href="/admin"><ShieldCheck size={18} /> Benutzer</a>
        </nav>
      </aside>

      <section className="adminContent">
        <header className="adminHeader">
          <div>
            <p className="eyebrow">Custom Admin</p>
            <h1>Einstellungen</h1>
          </div>
          <div className="adminActions">
            <span>{user.email}</span>
            <button className="button compact" type="button">
              <Save size={18} /> Speichern
            </button>
            <button className="button compact ghost" type="button" onClick={signOut}>
              <LogOut size={18} /> Logout
            </button>
          </div>
        </header>

        <div className="adminNotice">
          <KeyRound size={20} />
          <span>Secrets bleiben serverseitig. Public Settings werden getrennt fuer die Website bereitgestellt.</span>
        </div>

        <div className="settingsGrid">
          {settingsGroups.map((group) => (
            <article className="settingsCard" key={group}>
              <h2>{group}</h2>
              <p>{getDescription(group)}</p>
              <button type="button">Bearbeiten</button>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}

function AdminLogin({
  error,
  onSignIn
}: {
  error: string | null;
  onSignIn: (email: string, password: string) => Promise<void>;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsSubmitting(true);
    await onSignIn(email, password);
    setIsSubmitting(false);
  }

  return (
    <main className="loginShell">
      <form className="loginPanel" onSubmit={handleSubmit}>
        <img src="/seo-pictures-logo.svg" alt="SEO Pictures" />
        <div>
          <p className="eyebrow">Admin Login</p>
          <h1>Einloggen</h1>
        </div>
        <label>
          E-Mail
          <input autoComplete="email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
        </label>
        <label>
          Passwort
          <input
            autoComplete="current-password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </label>
        {!envStatus.hasSupabaseUrl || !envStatus.hasSupabaseAnonKey ? (
          <div className="loginWarning">
            <strong>Supabase Env fehlt im Build.</strong>
            <span>Erwartet: VITE_SUPABASE_URL und VITE_SUPABASE_ANON_KEY.</span>
          </div>
        ) : null}
        {error ? <p className="loginError">{error}</p> : null}
        <button className="button" type="submit" disabled={isSubmitting}>
          <LogIn size={18} /> {isSubmitting ? 'Pruefe...' : 'Einloggen'}
        </button>
      </form>
    </main>
  );
}

function getDescription(group: string) {
  const descriptions: Record<string, string> = {
    Allgemein: 'Logo, Sprachen, Kontakt, Footer, globale CTAs.',
    SEO: 'Meta Defaults, hreflang, Schema, Sitemap und Robots.',
    Tracking: 'Umami, GA4, GTM, Meta Pixel und LinkedIn.',
    'Conversion Events': 'CTA-, Formular- und Kampagnenereignisse.',
    'CDN & Media': 'R2, CDN URL, Bildvarianten und Cache Defaults.',
    Sentry: 'Web/Admin DSN, Environment, Sampling und PII.',
    Formulare: 'Empfaenger, Pflichtfelder, Spam-Schutz und Danke-Seiten.',
    Integrationen: 'Bitrix24, Booking, SMTP und Webhooks.',
    Rechtliches: 'Cookie-Hinweise, Datenschutz, Impressum und Consent.'
  };

  return descriptions[group] ?? 'Konfigurierbarer Admin-Bereich.';
}
